import React, { useCallback, useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { Loader2Icon, PlusIcon, LogOutIcon, XIcon } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { adminFetch, type ManagedUser } from '../lib/adminApi'
import { USER_ROLES } from '../lib/permissions'
import { PlatformUsersPanel } from '../components/platform/PlatformUsersPanel'

type OwnerInfo = {
  id: string
  email: string
  first_name?: string | null
  last_name?: string | null
  phone?: string | null
  role?: string
}

type TenantRow = {
  id: string
  name: string
  slug: string
  status: string
  billing_plan?: string | null
  settings?: { notes?: string } | null
  created_at?: string
  owner?: OwnerInfo | null
}

type OwnerForm = {
  first_name: string
  last_name: string
  email: string
  phone: string
  createMode: 'invite' | 'password'
  password: string
  passwordConfirm: string
}

const emptyOwner = (): OwnerForm => ({
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  createMode: 'invite',
  password: '',
  passwordConfirm: '',
})

function ownerPayload(form: OwnerForm): {
  first_name: string
  last_name: string
  email: string
  phone?: string
  send_invite: boolean
  password?: string
} {
  const base = {
    first_name: form.first_name.trim(),
    last_name: form.last_name.trim(),
    email: form.email.trim(),
    phone: form.phone.trim() || undefined,
  }
  if (form.createMode === 'invite') {
    return { ...base, send_invite: true }
  }
  return { ...base, send_invite: false, password: form.password }
}

function validateOwner(form: OwnerForm): string | null {
  if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
    return 'Owner first name, last name, and email are required'
  }
  if (form.createMode === 'password') {
    if (form.password.length < 8) return 'Password must be at least 8 characters'
    if (form.password !== form.passwordConfirm) return 'Passwords do not match'
  }
  return null
}

function OwnerFields({
  value,
  onChange,
  idPrefix,
}: {
  value: OwnerForm
  onChange: (next: OwnerForm) => void
  idPrefix: string
}) {
  const patch = (p: Partial<OwnerForm>) => onChange({ ...value, ...p })
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor={`${idPrefix}-fn`}>
            First name
          </label>
          <input
            id={`${idPrefix}-fn`}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={value.first_name}
            onChange={(e) => patch({ first_name: e.target.value })}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor={`${idPrefix}-ln`}>
            Last name
          </label>
          <input
            id={`${idPrefix}-ln`}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={value.last_name}
            onChange={(e) => patch({ last_name: e.target.value })}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor={`${idPrefix}-email`}>
            Email
          </label>
          <input
            id={`${idPrefix}-email`}
            type="email"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={value.email}
            onChange={(e) => patch({ email: e.target.value })}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor={`${idPrefix}-phone`}>
            Phone <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            id={`${idPrefix}-phone`}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={value.phone}
            onChange={(e) => patch({ phone: e.target.value })}
          />
        </div>
      </div>
      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-700">Login</p>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="radio"
            checked={value.createMode === 'invite'}
            onChange={() => patch({ createMode: 'invite' })}
          />
          Send invite email (they set their own password)
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="radio"
            checked={value.createMode === 'password'}
            onChange={() => patch({ createMode: 'password' })}
          />
          Set a temporary password
        </label>
        {value.createMode === 'password' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={value.password}
                onChange={(e) => patch({ password: e.target.value })}
                minLength={8}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm password</label>
              <input
                type="password"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={value.passwordConfirm}
                onChange={(e) => patch({ passwordConfirm: e.target.value })}
                minLength={8}
                required
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}


function FormModal({
  title,
  subtitle,
  onClose,
  children,
  wide = false,
}: {
  title: string
  subtitle?: string
  onClose: () => void
  children: React.ReactNode
  wide?: boolean
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div
        className={`bg-white rounded-lg shadow-xl w-full max-h-[90vh] flex flex-col ${wide ? 'max-w-3xl' : 'max-w-2xl'}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="platform-console-modal-title"
      >
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-gray-200 shrink-0">
          <div>
            <h2 id="platform-console-modal-title" className="text-lg font-semibold text-gray-900">
              {title}
            </h2>
            {subtitle ? <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
            aria-label="Close"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4">{children}</div>
      </div>
    </div>
  )
}
export function PlatformConsole() {
  const { session, isSuperAdmin, canCreateSuperAdmins, permissionLoading, signOut } = useAuth()
  const [tenants, setTenants] = useState<TenantRow[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [billingPlan, setBillingPlan] = useState('')
  const [notes, setNotes] = useState('')
  const [owner, setOwner] = useState<OwnerForm>(emptyOwner)
  const [assignFor, setAssignFor] = useState<string | null>(null)
  const [assignOwner, setAssignOwner] = useState<OwnerForm>(emptyOwner)
  const [showCreate, setShowCreate] = useState(false)
  const [manageTenant, setManageTenant] = useState<TenantRow | null>(null)
  const [tenantUsers, setTenantUsers] = useState<ManagedUser[]>([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [showAddUser, setShowAddUser] = useState(false)
  const [newUser, setNewUser] = useState<OwnerForm>(emptyOwner)
  const [newUserRole, setNewUserRole] = useState('admin')
  const [makeOwnerOnCreate, setMakeOwnerOnCreate] = useState(false)
  const [detailTab, setDetailTab] = useState<'overview' | 'users'>('overview')
  const [consoleTab, setConsoleTab] = useState<'tenants' | 'users'>('tenants')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [editSlug, setEditSlug] = useState('')
  const [editStatus, setEditStatus] = useState('active')
  const [editBillingPlan, setEditBillingPlan] = useState('')
  const [editNotes, setEditNotes] = useState('')
  const [replaceOwner, setReplaceOwner] = useState(false)
  const [editOwner, setEditOwner] = useState<OwnerForm>(emptyOwner)

  const load = useCallback(async () => {
    if (!session) return
    setLoading(true)
    setError(null)
    try {
      const data = (await adminFetch('/api/admin/tenants', session)) as { tenants?: TenantRow[] }
      const list = data.tenants || []
      setTenants(list)
      setManageTenant((prev) => (prev ? list.find((x) => x.id === prev.id) || prev : null))
    } catch (e: any) {
      setError(e.message || 'Failed to load tenants')
    } finally {
      setLoading(false)
    }
  }, [session])

  useEffect(() => {
    if (isSuperAdmin && session) load()
  }, [isSuperAdmin, session, load])

  if (permissionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  if (!isSuperAdmin) return <Navigate to="/access-denied" replace />

  const create = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!session) return
    const ownerErr = validateOwner(owner)
    if (ownerErr) {
      setError(ownerErr)
      return
    }
    if (!name.trim() || !slug.trim()) {
      setError('Organization name and slug are required')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await adminFetch('/api/admin/tenants', session, {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          slug: slug.trim(),
          billing_plan: billingPlan.trim() || undefined,
          notes: notes.trim() || undefined,
          owner: ownerPayload(owner),
        }),
      })
      setName('')
      setSlug('')
      setBillingPlan('')
      setNotes('')
      setOwner(emptyOwner())
      setShowCreate(false)
      await load()
    } catch (err: any) {
      setError(err.message || 'Create failed')
    } finally {
      setSaving(false)
    }
  }

  const setStatus = async (id: string, status: string) => {
    if (!session) return
    try {
      await adminFetch(`/api/admin/tenants/${id}`, session, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      })
      await load()
    } catch (err: any) {
      setError(err.message || 'Update failed')
    }
  }


  const openEdit = (t: TenantRow) => {
    setShowCreate(false)
    setAssignFor(null)
    setEditingId(t.id)
    setEditName(t.name || '')
    setEditSlug(t.slug || '')
    setEditStatus(t.status || 'active')
    setEditBillingPlan(t.billing_plan || '')
    setEditNotes((t.settings && t.settings.notes) || '')
    setReplaceOwner(false)
    setEditOwner(emptyOwner())
    setError(null)
  }

  const submitEdit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!session || !editingId) return
    if (!editName.trim() || !editSlug.trim()) {
      setError('Organization name and slug are required')
      return
    }
    if (replaceOwner) {
      const ownerErr = validateOwner(editOwner)
      if (ownerErr) {
        setError(ownerErr)
        return
      }
    }
    setSaving(true)
    setError(null)
    try {
      const body: Record<string, unknown> = {
        name: editName.trim(),
        slug: editSlug.trim(),
        status: editStatus,
        billing_plan: editBillingPlan.trim() || null,
        notes: editNotes.trim() || '',
      }
      if (replaceOwner) {
        body.owner = ownerPayload(editOwner)
        body.replace_owner = true
      }
      await adminFetch(`/api/admin/tenants/${editingId}`, session, {
        method: 'PATCH',
        body: JSON.stringify(body),
      })
      setEditingId(null)
      setReplaceOwner(false)
      setEditOwner(emptyOwner())
      await load()
    } catch (err: any) {
      setError(err.message || 'Update failed')
    } finally {
      setSaving(false)
    }
  }
  const submitAssign = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!session || !assignFor) return
    const ownerErr = validateOwner(assignOwner)
    if (ownerErr) {
      setError(ownerErr)
      return
    }
    setSaving(true)
    setError(null)
    try {
      await adminFetch(`/api/admin/tenants/${assignFor}`, session, {
        method: 'PATCH',
        body: JSON.stringify({ owner: ownerPayload(assignOwner), replace_owner: true }),
      })
      setAssignFor(null)
      setAssignOwner(emptyOwner())
      await load()
    } catch (err: any) {
      setError(err.message || 'Assign owner failed')
    } finally {
      setSaving(false)
    }
  }


  const loadTenantUsers = async (tenantId: string) => {
    if (!session) return
    setUsersLoading(true)
    try {
      const data = (await adminFetch('/api/admin/users?tenant_id=' + encodeURIComponent(tenantId), session)) as {
        users?: ManagedUser[]
      }
      setTenantUsers(data.users || [])
    } catch (e: any) {
      setError(e.message || 'Failed to load users')
    } finally {
      setUsersLoading(false)
    }
  }

  const openTenantModule = async (t: TenantRow, tab: 'overview' | 'users' = 'overview') => {
    setShowCreate(false)
    setEditingId(null)
    setAssignFor(null)
    setManageTenant(t)
    setDetailTab(tab)
    setShowAddUser(false)
    setNewUser(emptyOwner())
    setNewUserRole('admin')
    setMakeOwnerOnCreate(false)
    setError(null)
    if (tab === 'users') await loadTenantUsers(t.id)
  }

  const setAsOwner = async (userId: string) => {
    if (!session || !manageTenant) return
    setSaving(true)
    setError(null)
    try {
      await adminFetch('/api/admin/tenants/' + manageTenant.id, session, {
        method: 'PATCH',
        body: JSON.stringify({ owner_user_id: userId }),
      })
      await load()
      const refreshed = (await adminFetch('/api/admin/tenants', session)) as { tenants?: TenantRow[] }
      const next = (refreshed.tenants || []).find((x) => x.id === manageTenant.id) || manageTenant
      setManageTenant(next)
      await loadTenantUsers(manageTenant.id)
    } catch (e: any) {
      setError(e.message || 'Failed to set owner')
    } finally {
      setSaving(false)
    }
  }

  const deactivateTenantUser = async (u: ManagedUser) => {
    if (!session || !manageTenant) return
    setSaving(true)
    setError(null)
    try {
      await adminFetch('/api/admin/users/' + u.id, session, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: false }),
      })
      await loadTenantUsers(manageTenant.id)
    } catch (e: any) {
      setError(e.message || 'Failed to deactivate')
    } finally {
      setSaving(false)
    }
  }

  const deleteTenantUser = async (u: ManagedUser) => {
    if (!session || !manageTenant) return
    if (!window.confirm('Permanently delete login for ' + u.email + '? This cannot be undone.')) return
    setSaving(true)
    setError(null)
    try {
      await adminFetch('/api/admin/users/' + u.id, session, { method: 'DELETE' })
      await load()
      await loadTenantUsers(manageTenant.id)
      const refreshed = (await adminFetch('/api/admin/tenants', session)) as { tenants?: TenantRow[] }
      const next = (refreshed.tenants || []).find((x) => x.id === manageTenant.id)
      if (next) setManageTenant(next)
    } catch (e: any) {
      setError(e.message || 'Failed to delete login')
    } finally {
      setSaving(false)
    }
  }

  const createTenantUser = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!session || !manageTenant) return
    const errMsg = validateOwner(newUser)
    if (errMsg) {
      setError(errMsg)
      return
    }
    setSaving(true)
    setError(null)
    try {
      const payload = ownerPayload(newUser)
      const body: Record<string, unknown> = {
        email: payload.email,
        first_name: payload.first_name,
        last_name: payload.last_name,
        phone: payload.phone || null,
        role: newUserRole,
        tenant_id: manageTenant.id,
        send_invite: !!payload.send_invite,
      }
      if (payload.password) {
        body.password = payload.password
        body.send_invite = false
      }
      const created = await adminFetch('/api/admin/users', session, {
        method: 'POST',
        body: JSON.stringify(body),
      })
      const newId = created.user?.id as string | undefined
      if (makeOwnerOnCreate && newId) {
        await adminFetch('/api/admin/tenants/' + manageTenant.id, session, {
          method: 'PATCH',
          body: JSON.stringify({ owner_user_id: newId }),
        })
      }
      setShowAddUser(false)
      setNewUser(emptyOwner())
      setMakeOwnerOnCreate(false)
      await load()
      await loadTenantUsers(manageTenant.id)
      const refreshed = (await adminFetch('/api/admin/tenants', session)) as { tenants?: TenantRow[] }
      const next = (refreshed.tenants || []).find((x) => x.id === manageTenant.id)
      if (next) setManageTenant(next)
    } catch (err: any) {
      setError(err.message || 'Failed to create user')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gray-50">
      <header className="bg-white border-b border-gray-200 shrink-0">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Platform console</h1>
            <p className="text-sm text-gray-500">Super admin — tenants and owners</p>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <button
              type="button"
              onClick={() => signOut()}
              className="inline-flex items-center gap-1.5 text-gray-600 hover:text-gray-900"
            >
              <LogOutIcon className="w-4 h-4" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>
          )}

          {!manageTenant ? (
          <>
          <div className="border-b border-gray-200 flex gap-4 -mt-2 mb-2">
            <button
              type="button"
              onClick={() => setConsoleTab('tenants')}
              className={'pb-2 text-sm font-medium border-b-2 -mb-px ' + (consoleTab === 'tenants' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800')}
            >
              Tenants
            </button>
            <button
              type="button"
              onClick={() => setConsoleTab('users')}
              className={'pb-2 text-sm font-medium border-b-2 -mb-px ' + (consoleTab === 'users' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800')}
            >
              Users
            </button>
          </div>

          {consoleTab === 'users' ? (
            <PlatformUsersPanel
              session={session}
              canCreateSuperAdmins={!!canCreateSuperAdmins}
              tenants={tenants}
              onError={setError}
            />
          ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Tenants</h2>
                <p className="text-xs text-gray-500">
                  {loading ? 'Loading…' : `${tenants.length} organization${tenants.length === 1 ? '' : 's'}`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => load()}
                  disabled={loading}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Refresh
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingId(null)
                    setAssignFor(null)
                    setShowCreate(true)
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <PlusIcon className="w-4 h-4" />
                  New tenant
                </button>
              </div>
            </div>
            {loading ? (
              <div className="p-8 text-center">
                <Loader2Icon className="w-6 h-6 animate-spin mx-auto text-blue-600" />
              </div>
            ) : tenants.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-500">No tenants yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-4 py-3">Organization</th>
                      <th className="px-4 py-3">Slug</th>
                      <th className="px-4 py-3">Owner</th>
                      <th className="px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {tenants.map((t) => (
                      <tr key={t.id} className="align-top hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            className="text-left font-medium text-blue-700 hover:underline"
                            onClick={() => openTenantModule(t, 'overview')}
                          >
                            {t.name}
                          </button>
                          {t.billing_plan && (
                            <div className="text-xs text-gray-500">{t.billing_plan}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{t.slug}</td>
                        <td className="px-4 py-3">
                          {t.owner ? (
                            <div>
                              <div className="font-medium text-gray-900">
                                {[t.owner.first_name, t.owner.last_name].filter(Boolean).join(' ') || '—'}
                              </div>
                              <div className="text-xs text-gray-500">{t.owner.email}</div>
                              {t.owner.phone && (
                                <div className="text-xs text-gray-400">{t.owner.phone}</div>
                              )}
                            </div>
                          ) : (
                            <span className="text-amber-700 text-xs font-medium">No owner</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                              t.status === 'active'
                                ? 'bg-green-100 text-green-800'
                                : t.status === 'suspended'
                                  ? 'bg-amber-100 text-amber-800'
                                  : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {t.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          )}
          </>
          ) : (
          <div className="space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <button
                  type="button"
                  onClick={() => {
                    setManageTenant(null)
                    setShowAddUser(false)
                    setDetailTab('overview')
                  }}
                  className="text-sm text-blue-700 hover:underline mb-2"
                >
                  ← All tenants
                </button>
                <h2 className="text-2xl font-bold text-gray-900">{manageTenant.name}</h2>
                <p className="text-sm text-gray-500">
                  {manageTenant.slug}
                  {' · '}
                  <span className={manageTenant.status === 'active' ? 'text-green-700 font-medium' : 'text-amber-700 font-medium'}>
                    {manageTenant.status}
                  </span>
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => openEdit(manageTenant)}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Edit
                </button>
                {manageTenant.status === 'active' ? (
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => setStatus(manageTenant.id, 'suspended')}
                    className="px-3 py-1.5 text-sm border border-amber-300 rounded-lg text-amber-800 bg-amber-50 hover:bg-amber-100 disabled:opacity-50"
                  >
                    Suspend
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => setStatus(manageTenant.id, 'active')}
                    className="px-3 py-1.5 text-sm border border-green-300 rounded-lg text-green-800 bg-green-50 hover:bg-green-100 disabled:opacity-50"
                  >
                    Activate
                  </button>
                )}
              </div>
            </div>

            <div className="border-b border-gray-200 flex gap-4">
              <button
                type="button"
                onClick={() => setDetailTab('overview')}
                className={'pb-2 text-sm font-medium border-b-2 -mb-px ' + (detailTab === 'overview' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800')}
              >
                Overview
              </button>
              <button
                type="button"
                onClick={async () => {
                  setDetailTab('users')
                  await loadTenantUsers(manageTenant.id)
                }}
                className={'pb-2 text-sm font-medium border-b-2 -mb-px ' + (detailTab === 'users' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800')}
              >
                Users
              </button>
            </div>

            {detailTab === 'overview' && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-xs uppercase text-gray-500 font-semibold">Slug</div>
                    <div className="text-gray-900">{manageTenant.slug}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase text-gray-500 font-semibold">Status</div>
                    <div className="text-gray-900">{manageTenant.status}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase text-gray-500 font-semibold">Billing plan</div>
                    <div className="text-gray-900">{manageTenant.billing_plan || '—'}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase text-gray-500 font-semibold">Owner</div>
                    <div className="text-gray-900">
                      {manageTenant.owner ? (
                        <>
                          {[manageTenant.owner.first_name, manageTenant.owner.last_name].filter(Boolean).join(' ') || '—'}
                          <div className="text-xs text-gray-500">{manageTenant.owner.email}</div>
                        </>
                      ) : (
                        <span className="text-amber-700">No owner</span>
                      )}
                    </div>
                  </div>
                  {manageTenant.settings?.notes ? (
                    <div className="md:col-span-2">
                      <div className="text-xs uppercase text-gray-500 font-semibold">Notes</div>
                      <div className="text-gray-900 whitespace-pre-wrap">{manageTenant.settings.notes}</div>
                    </div>
                  ) : null}
                </div>
                {!manageTenant.owner && (
                  <button
                    type="button"
                    className="text-sm text-blue-700 underline"
                    onClick={() => {
                      setAssignFor(manageTenant.id)
                      setAssignOwner(emptyOwner())
                    }}
                  >
                    Assign owner
                  </button>
                )}
              </div>
            )}

            {detailTab === 'users' && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
<div className="space-y-4">
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
              <span className="text-gray-500">Owner: </span>
              {manageTenant.owner ? (
                <span className="font-medium text-gray-900">
                  {[manageTenant.owner.first_name, manageTenant.owner.last_name].filter(Boolean).join(' ') || '—'}
                  {' · '}
                  {manageTenant.owner.email}
                </span>
              ) : (
                <span className="text-amber-700 font-medium">No owner set</span>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">Users</h3>
              <button
                type="button"
                onClick={() => setShowAddUser((v) => !v)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <PlusIcon className="w-4 h-4" />
                {showAddUser ? 'Hide form' : 'Add user'}
              </button>
            </div>

            {showAddUser && (
              <form onSubmit={createTenantUser} className="border border-gray-200 rounded-lg p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      value={newUserRole}
                      onChange={(e) => setNewUserRole(e.target.value)}
                    >
                      {USER_ROLES.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
                      <input
                        type="checkbox"
                        checked={makeOwnerOnCreate}
                        onChange={(e) => {
                          setMakeOwnerOnCreate(e.target.checked)
                          if (e.target.checked) setNewUserRole('admin')
                        }}
                      />
                      Set as tenant owner
                    </label>
                  </div>
                </div>
                <OwnerFields idPrefix="manage-user" value={newUser} onChange={setNewUser} />
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? <Loader2Icon className="w-4 h-4 animate-spin" /> : <PlusIcon className="w-4 h-4" />}
                  Create user
                </button>
              </form>
            )}

            {usersLoading ? (
              <div className="py-8 text-center">
                <Loader2Icon className="w-6 h-6 animate-spin mx-auto text-blue-600" />
              </div>
            ) : tenantUsers.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center">No users in this tenant yet.</p>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2">Name</th>
                      <th className="px-3 py-2">Email</th>
                      <th className="px-3 py-2">Role</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {tenantUsers.map((u) => {
                      const isOwner = manageTenant.owner?.id === u.id
                      return (
                        <tr key={u.id} className="align-top">
                          <td className="px-3 py-2 font-medium text-gray-900">
                            {[u.first_name, u.last_name].filter(Boolean).join(' ') || '—'}
                            {isOwner && (
                              <span className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">
                                Owner
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-gray-600">{u.email}</td>
                          <td className="px-3 py-2 text-gray-600">{u.role}</td>
                          <td className="px-3 py-2">
                            <span
                              className={
                                'text-xs font-semibold ' +
                                (u.is_active !== false ? 'text-green-700' : 'text-gray-500')
                              }
                            >
                              {u.is_active !== false ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-3 py-2 space-y-1">
                            {!isOwner && (
                              <button
                                type="button"
                                disabled={saving}
                                className="block text-blue-700 underline text-xs disabled:opacity-50"
                                onClick={() => setAsOwner(u.id)}
                              >
                                Make owner
                              </button>
                            )}
                            {u.is_active !== false && (
                              <button
                                type="button"
                                disabled={saving}
                                className="block text-amber-700 underline text-xs disabled:opacity-50"
                                onClick={() => deactivateTenantUser(u)}
                              >
                                Deactivate
                              </button>
                            )}
                            <button
                              type="button"
                              disabled={saving}
                              className="block text-red-700 underline text-xs disabled:opacity-50"
                              onClick={() => deleteTenantUser(u)}
                            >
                              Delete login
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
              </div>
            )}
          </div>
          )}
        </div>
      </main>

      {showCreate && (
        <FormModal
          title="Create tenant"
          subtitle="Organization plus a required admin owner."
          onClose={() => setShowCreate(false)}
        >
          <form onSubmit={create} className="space-y-6">
            <section className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">Organization</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Display name</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Slug (short code)</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    placeholder="acme-corp"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Billing plan <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={billingPlan}
                    onChange={(e) => setBillingPlan(e.target.value)}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Internal notes <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>
              </div>
            </section>
            <section className="space-y-4 border-t border-gray-100 pt-6">
              <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">Owner admin</h3>
              <OwnerFields idPrefix="create" value={owner} onChange={setOwner} />
            </section>
            <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? <Loader2Icon className="w-4 h-4 animate-spin" /> : <PlusIcon className="w-4 h-4" />}
                Create tenant + owner
              </button>
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
            </div>
          </form>
        </FormModal>
      )}

      {editingId && (
        <FormModal
          title="Edit tenant"
          subtitle="Update organization details or replace the owner admin."
          onClose={() => setEditingId(null)}
        >
          <form onSubmit={submitEdit} className="space-y-6">
            <section className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">Organization</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Display name</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Slug (short code)</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={editSlug}
                    onChange={(e) => setEditSlug(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value)}
                  >
                    <option value="active">active</option>
                    <option value="suspended">suspended</option>
                    <option value="deleted">deleted</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Billing plan <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={editBillingPlan}
                    onChange={(e) => setEditBillingPlan(e.target.value)}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Internal notes <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={2}
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                  />
                </div>
              </div>
            </section>
            <section className="space-y-4 border-t border-gray-100 pt-6">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
                <input
                  type="checkbox"
                  checked={replaceOwner}
                  onChange={(e) => setReplaceOwner(e.target.checked)}
                />
                Replace owner admin (creates a new admin login)
              </label>
              {replaceOwner && <OwnerFields idPrefix="edit-owner" value={editOwner} onChange={setEditOwner} />}
            </section>
            <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? <Loader2Icon className="w-4 h-4 animate-spin" /> : null}
                Save changes
              </button>
              <button
                type="button"
                onClick={() => setEditingId(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
            </div>
          </form>
        </FormModal>
      )}

      

{assignFor && (
        <FormModal
          title="Assign owner"
          subtitle="Create an admin owner for this tenant."
          onClose={() => setAssignFor(null)}
        >
          <form onSubmit={submitAssign} className="space-y-6">
            <OwnerFields idPrefix="assign" value={assignOwner} onChange={setAssignOwner} />
            <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? <Loader2Icon className="w-4 h-4 animate-spin" /> : null}
                Save owner
              </button>
              <button
                type="button"
                onClick={() => setAssignFor(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
            </div>
          </form>
        </FormModal>
      )}
    </div>
  )
}

export default PlatformConsole